import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import EmailIcon from '@mui/icons-material/Email';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/components/ui/use-toast';

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setOtpState, setTempToken } = useAuthStore();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: t('common.error'),
        description: t('auth.enterEmail'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/auth/login/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.otpFailed'));
      }

      setOtpState({
        email,
        expiresAt: data.data.expiresAt,
        purpose: 'login',
      });
      setTempToken(data.data.tempToken);

      toast({
        title: t('auth.otpSent'),
        description: t('auth.checkEmail'),
        variant: 'success',
      });

      navigate('/verify-otp');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.somethingWrong'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/CareTrust_APP_Logo.png"
          alt="CareTrust"
          sx={{
            width: 100,
            height: 100,
            mb: 2,
          }}
        />
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          {t('auth.welcomeBack')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('auth.enterEmailToSignIn')}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          id="email"
          name="email"
          type="email"
          label={t('auth.email')}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          autoComplete="email"
          autoFocus
          InputProps={{
            startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{ mb: 3, py: 1.5 }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('auth.sendVerificationCode')
          )}
        </Button>

        <Typography variant="body2" align="center" color="text.secondary">
          {t('auth.noAccount')}{' '}
          <Link component={RouterLink} to="/register" underline="hover">
            {t('auth.signUp')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
